import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Box, Chip, Paper, useMediaQuery, useTheme } from '@mui/material';
import {
  PlayCircle as PlayCircleIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import DramaCard from '../components/DramaCard';

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const [featuredDramas, setFeaturedDramas] = useState([]);
  
  const popularCategories = ['悬疑', '爱情', '喜剧', '科幻', '恐怖', '动作'];

  // 从 API 获取剧集数据
  useEffect(() => {
    const fetchDramas = async () => {
      try {
        console.log('正在获取剧集数据...');
        const response = await fetch('/api/dramas');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('API响应数据:', data);
        
        if (data.success && Array.isArray(data.data)) {
          // 将 API 数据转换为前端需要的格式
          const formattedDramas = data.data.map(drama => ({
            id: drama.id,
            title: drama.name,
            name: drama.name, // 保持兼容性，同时使用统一的 title 字段
            description: drama.description,
            poster: drama.poster && drama.poster.trim() !== "" ? drama.poster : 'https://placehold.co/300x450?text=Drama',
            category: drama.category || '悬疑', // 使用API返回的分类
            views: drama.view_count || 0,
            viewCount: drama.view_count || 0, // 保持兼容性，同时使用统一的 views 字段
            favoriteCount: drama.favorite_count || 0, // 新增：添加收藏次数字段
            likeCount: drama.favorite_count || 0, // 保持兼容性
            comments: 0,
            episodes: drama.episodes // 新增：添加剧集列表字段
          }));
          
          console.log('格式化后的剧集数据:', formattedDramas);
          setFeaturedDramas(formattedDramas);
        }
      } catch (error) {
        console.error('获取剧集数据失败:', error);
        
        // 降级到示例数据
        const fallbackDramas = [
          {
            id: '1',
            title: '热门剧集1',
            description: '这是一个热门剧集的描述',
            poster: 'https://via.placeholder.com/300x450',
            category: '悬疑',
            views: 1200,
            comments: 89,
          },
          {
            id: '2',
            title: '最新上线剧集',
            description: '这是一个最新上线剧集的描述',
            poster: 'https://via.placeholder.com/300x450',
            category: '爱情',
            views: 850,
            comments: 45,
          },
          {
            id: '3',
            title: '高分推荐',
            description: '这是一个高分推荐剧集的描述',
            poster: 'https://via.placeholder.com/300x450',
            category: '喜剧',
            views: 1500,
            comments: 120,
          },
        ];
        
        console.log('使用降级数据:', fallbackDramas);
        setFeaturedDramas(fallbackDramas);
      }
    };

    fetchDramas();
  }, []);

  // 处理分类标签点击
  const handleCategoryClick = (category) => {
    console.log('分类标签点击事件:', category);
    // 这里可以添加分类搜索逻辑
    history.push(`/search?category=${category}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 2 }}>


      {/* 热门剧集 */}
      <Box sx={{ mb: 4 }}>
        {featuredDramas.length > 0 ? (
          <Grid container spacing={2}>
            {featuredDramas.map((drama) => (
              <Grid item xs={12} sm={6} md={4} key={drama.id}>
                <DramaCard drama={drama} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              暂无剧集数据
            </Typography>
          </Box>
        )}
      </Box>

      {/* 分类推荐 - 修复了按钮点击事件 */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h2" 
          sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
        >
          热门分类
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {popularCategories.map((category) => (
            <Chip
              key={category}
              label={category}
              size={isMobile ? 'small' : 'medium'}
              color="primary"
              variant="outlined"
              sx={{ 
                cursor: 'pointer',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
              onClick={() => handleCategoryClick(category)}
              id={`category-${category}`}
            />
          ))}
        </Box>
      </Box>

      {/* 平台介绍 */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h2" 
          sx={{ mb: 3, fontSize: isMobile ? '1.25rem' : '1.5rem' }}
        >
          关于平台
        </Typography>
        <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
          <Typography variant="body1" paragraph fontSize={isMobile ? '0.875rem' : '1rem'}>
            短剧播放平台是一个专注于提供优质剧集内容的在线视频平台。我们致力于为用户提供多样化的剧集选择，包括悬疑、爱情、喜剧、科幻等多个类别。
          </Typography>
          <Typography variant="body1" paragraph fontSize={isMobile ? '0.875rem' : '1rem'}>
            平台采用现代化的技术架构，提供流畅的播放体验和丰富的互动功能。用户可以浏览剧集、收藏喜爱的内容、发表评论和分享给好友。
          </Typography>
          <Typography variant="body1" fontSize={isMobile ? '0.875rem' : '1rem'}>
            加入我们，开启您的剧集探索之旅！
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Home;
